import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'recompose';
import BodyClassName from 'react-body-classname';
import Segment from '@sparefoot/segment-react';
import { withFixedSegmentCategory } from '@sparefoot/react-core/segment';
import { withViewportWidth } from 'decorators';
import dialNumber from 'utils/dialNumber';
import { Text, H3, Paragraph } from 'components/core/Typography';
import { Button } from 'components/core/Button';
import { Phone, Filter, Chevron } from 'components/svg';

import './SearchTray.scss';

export const enhance = compose(
	withFixedSegmentCategory('sticky tray'),
	withViewportWidth
);

export function SearchTray({
	visitId,
	searchId,
	onShowSearchFilterModal,
	searchLocation,
	getLocalCityPhoneNumber,
	stickyTrayPhoneNumber,
	delay = 500,
	invisible
}) {
	const [isExpanded, setIsExpanded] = React.useState(true);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isVisible, setIsVisible] = React.useState(false);


	const timeoutRef = React.useRef(null);
	const { formattedNumber, number } = stickyTrayPhoneNumber || {};

	// Start delayed render timer on component mount
	React.useEffect(() => {
		clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			setIsVisible(true);
			Segment.track('sticky tray pop up', { searchId, visitId });
		}, delay);
		return () => clearTimeout(timeoutRef.current);
	}, []);

	// Dial the allocated phone number once we've received it
	React.useEffect(() => {
		if (number) {
			dialNumber(number);
			setIsLoading(false);
			Segment.track('number dialed', { visitId, searchId, number });
		}
	}, [number]);

	// Allocate a phone number if one has not been already
	const buttonHandler = () => {
		if (!number) {
			setIsLoading(true);
			getLocalCityPhoneNumber(searchLocation.city, searchLocation.state); // Should only happen once
		} else {
			// If a desktop user wants to dial, they can this way
			dialNumber(number);
		}
	};

	const titleText = "Can't Decide?";
	const subtitleText = 'Call us to help you find something faster.';

	const loadingText = 'Loading...';
	const mobileCallText = isLoading ? loadingText : formattedNumber || 'Call Now';

	return (
		<div className={classnames('search-tray', { hidden: !isVisible, visible: isVisible, invisible })}>
			<BodyClassName className="with-sticky-tray-padding" />
			<div className="content">
				<Button
					className="tab"
					segmentLabel={`tray ${isExpanded ? 'open' : 'close'}`}
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<Chevron direction={isExpanded ? 'down' : 'up'} />
				</Button>

				<div className={classnames('slider', { open: isExpanded, closed: !isExpanded })}>
					<div className="slider-inner">
						<H3 color="white" className="title">{titleText}</H3>
						<Paragraph color="lightGray" className="text">
							{subtitleText}
						</Paragraph>
					</div>
				</div>

				<div className={classnames('buttons-container')}>
					<Button
						primary
						segmentLabel="call now"
						fullWidth
						onClick={buttonHandler}
						className={classnames(
							'call-now-btn',
						)}
					>
						{!isLoading && <Phone />}
						{mobileCallText}
					</Button>
					<Text color="white">or</Text>
					<Button
						secondary
						onClick={onShowSearchFilterModal}
						segmentLabel="filter"
						className={classnames('filter-btn')}
					>
						<Filter />
						Filter
					</Button>
				</div>
			</div>
		</div>
	);
}

SearchTray.propTypes = {
	visitId: PropTypes.string,
	searchId: PropTypes.string,
	onShowSearchFilterModal: PropTypes.func.isRequired,
	getLocalCityPhoneNumber: PropTypes.func.isRequired,
	stickyTrayPhoneNumber: PropTypes.object,
	searchLocation: PropTypes.object,
	delay: PropTypes.number,
	invisible: PropTypes.bool
};

export default enhance(SearchTray);
